<template>
	<v-row>
		<v-col>

			<v-form
					v-model="formIsValid.pricingForm"
					ref="pricingForm"
					class="mb-8"
					new-forms
			>
				<!-- Pricing -->
				<ui-box
						:form="$refs.pricingForm"
						:eager="!!localPass.id"
						:additional-validations="[hasPrices]"
						new-forms
				>

					<form-title title="Prices">
						This is where you define how much to sell your passes for. If there are no
						attributes, simply add your price into the “Retail Price” field. If you would
						like to add attributes to your pass, toggle “Pricing Tiers” on to define and
						price your attributes. If you do not want to use any of the attributes listed,
						simply leave those fields blank.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="7" md="5" lg="4">
							<v-switch
									v-model="pricingStrategy"
									class="mt-3"
									dense
									false-value="pricing"
									hide-details="auto"
									inset
									label="Use pricing tiers?"
									true-value="tiers"
							></v-switch>
						</v-col>
						<v-col cols="12" sm="5" md="5" lg="4">
							<v-row v-if="!usePricingTiers">
								<v-col>
									<v-text-field
											v-model.number="localPass.retail_price"
											class="mt-4 mt-sm-0 rounded-lg required"
											hide-details="auto"
											label="Retail Price"
											min="0.01"
											outlined
											:prefix="currencyPrefix"
											:rules="rules.retail_price"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<v-row v-if="usePricingTiers">
						<v-col cols="12" md="6">
							<v-row v-for="attribute in attributes" :key="attribute.label">
								<v-col cols="4" sm="4" md="5">
									<v-text-field
										v-model.number="attribute.retail_price"
										v-two-decimals
										class="rounded-lg"
										hide-details="auto"
										hide-spin-buttons
										:label="attribute.label"
										outlined
										persistent-hint
										:prefix="currencyPrefix"
										type="number"
										@wheel.native.prevent
									></v-text-field>
								</v-col>
								 
								<v-col cols="8" sm="8" md="7">
									<v-text-field
										v-model="attribute.sub_title"
										class="rounded-lg"
										:hint="attribute.hint"
										:label="`${attribute.label} Subtitle`"
										outlined
										persistent-hint
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form
					v-model="formIsValid.feesForm"
					ref="feesForm"
					class="mb-8"
					new-forms
			>
				<!-- Pricing -->
				<ui-box :form="$refs.feesForm" :eager="!!localPass.id" new-forms>

					<form-title title="Convenience Fee">
						You can add a specific convenience fee to this pass. Please note that if you
						have defined a convenience fee on any channel that this pass is sold, the (2)
						convenience fees will be <em>added together upon checkout</em>.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="6">
							<v-row>
								<v-col cols="6">
									<v-select
											v-model="localPass.convenience_fee_type"
											class="rounded-lg"
											:items="[
													{name: 'Amount', value: 'fixed'},
													{name: 'Percent', value: 'percent'}
											]"
											hide-details="auto"
											item-text="name"
											label="Convenience Fee Type"
											outlined
									></v-select>
								</v-col>
								<v-col>
									<v-text-field
											v-model.number="localPass.convenience_fee"
											class="rounded-lg"
											hide-spin-buttons
											hide-details="auto"
											label="Fee"
											outlined
											:prefix="isFeeTypeAmount ? currencyPrefix : null"
											:suffix="isFeeTypePercent ? '%' : null"
											type="number"
											@wheel.native.prevent
									></v-text-field>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

					<v-row>
						<v-col>
							<h6>Remittance Override</h6>
							<span class="grey--text text--darken-1 text-body-2">
								By default, the remittance method (paid on redemption or paid on sale) is
								set on an individual item level. Here, you can override how your
								merchants will be paid for this specific pass.
							</span>
						</v-col>
					</v-row>
					<v-divider class="mb-6 mt-4 opacity-80"></v-divider>

					<v-row>
						<v-col cols="12" md="6">
							<v-select
									v-model="localPass.paid_on_override"
									class="rounded-lg required"
									hide-details="auto"
									:items="[
									{name: 'By Product', value: 'by_product'}, 
									{name: 'On Sale', value: 'sale'}, 
									{name: 'On Redemption', value: 'redemption'}, 
									{name: 'Don\'t pay', value: 'never'}
									]"
									item-text="name"
									label="Paid On Override"
									outlined
									:rules="rules.paid_on_override"
							></v-select>
						</v-col>
					</v-row>
				</ui-box>
				<v-divider class="mb-6 mt-4 opacity-80"></v-divider>
				<ui-box :form="$refs.feesForm" :eager="!!localPass.id" new-forms>

					<form-title title="Sales Tax">
						You can define a different sales tax rate for this specific pass, which would
						override any tax rate you have defined on any channel that this pass is sold.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" md="6">
							<v-text-field
									v-model.number="localPass.tax_rate"
									class="mt-4 mt-sm-0 rounded-lg"
									hide-details="auto"
									label="Sales Tax Rate"
									outlined
									suffix="%"
									type="number"
									@wheel.native.prevent
							></v-text-field>
						</v-col>
					</v-row>

				</ui-box>
			</v-form>

			<form-divider/>

			<v-form
					v-model="formIsValid.policiesForm"
					ref="policiesForm"
					class="mb-8"
					new-forms
			>
				<!-- Policies -->
				<ui-box :form="$refs.policiesForm" :eager="!!localPass.id" new-forms>

					<form-title title="Policies">
						Description about policies here...
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="5" md="6" lg="5">
							<v-select
								v-model="refundPolicyKey"
								class="rounded-lg required"
								:hint="refundPolicyHint"
								:items="refundPolicyItems"
								label="Select Refund Policy"
								outlined
								persistent-hint
								@change="refundPolicyKeyChange"
							></v-select>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-textarea
									v-model="localPass.refund_policy"
									auto-grow
									class="rounded-lg required"
									counter="1000"
									hide-details="auto"
									label="Refund Policy"
									maxlength="1000"
									outlined
									persistent-hint
									rows="4"
									:rules="rules.refund_policy"
									@input="refundPolicyChange"
							></v-textarea>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>


			<save-form
					:refs="$refs"
					:additional-validations="[isFormValid]"
					:is-saving="isSaving"
					new-forms
					:save-text="saveText"
					@cancel-action="cancelChanges"
					@save-action="savePrices"
			></save-form>


		</v-col>
	</v-row>
</template>

<script>
import customizationMixin from './Mixins/customizationMixin';

export default {
	name: 'CustomizePricing',
	mixins: [customizationMixin],
	data() {
		return {

			attributes: [],

			currencyPrefix: this.partner.currencyPrefix,

			formIsValid: {
				feesForm: false,
				pricingForm: false,
				policiesForm: false
			},
			pricingStrategy: 'pricing',
			refundPolicyItems: [
				{ 
					value: 'no-refunds', 
					text: 'No Refunds', 
					policy: 'Refunds are not allowed under any circumstances.'
				},
				{ 
					value: '24-hour', 
					text: 'Within 24 Hours of Purchase', 
					policy: 'Refunds are only allowed if requested within 24 hours of purchase.'
				},
				{ 
					value: 'first-use', 
					text: 'Before First Use Only', 
					policy: 'Refunds are allowed only if the pass or ticket has not been used.'
				},
				{ 
					value: 'partial-use', 
					text: 'Refund After Partial Use', 
					policy: 'Partial refunds are available if only a portion of the pass or ticket has been used.'
				},
				{ 
					value: 'unused', 
					text: 'Allowed for Unused Passes Only', 
					policy: 'Refunds are permitted if the pass or ticket remains completely unused.'
				},
				{ 
					value: 'event-cancellations', 
					text: 'Allowed for Event Cancellations Only', 
					policy: 'Refunds are provided exclusively when the event is canceled.'
				},
				{ 
					value: 'admin-approval', 
					text: 'With Admin Approval Only', 
					policy: 'Refunds are subject to review and approval by an administrator.'
				},
				{ 
					value: 'minus-fees', 
					text: 'Minus Processing Fees', 
					policy: 'Refunds are allowed, but processing or transaction fees will be deducted.'
				},
				{ 
					value: 'timeframe', 
					text: 'Within a Specific Timeframe', 
					policy: 'Refund requests must be made within _____________ before the pass expires.',
					hint: '(e.g., 7 Days Before Expiration)'
				},
				{ 
					value: '', 
					text: 'Custom Policy', 
					policy: '',
					hint: '(Please Specify)'
				}
			]
		}
	},
	mounted() {
		const isTimeframe = /^Refund requests must be made within (?!_+$)[a-zA-Z0-9]+ before the pass expires\.$/;

		this.rules = {
			...this.rules,
			retail_price: [
				v => !!v || 'Retail Price is required.',
				v => v >= 0.01 || 'Retail Price should be greater than 0.'
			],
			paid_on_override: [v => !!v || 'Paid On Override is required.'],
			refund_policy: [
				v => !!v || 'Refund policy is required.',
				v => this.refundPolicyKey !== 'timeframe' ||
					isTimeframe.test(v.trim()) ||
					'Please specify the timeframe'
			]
		};
		this.attributes =  this.localPass.attributes;
		this.pricingStrategy = this.localPass?.attributes?.filter(x=>!!x.retail_price)?.length > 0 ? 'tiers' : 'pricing';
		if(!this.localPass.convenience_fee_type) {
			this.localPass.convenience_fee_type = 'fixed';
		}
	},
	computed: {
		hasPrices: function () {
			if(this.usePricingTiers) {
				const validAttributes = Object.values(this.attributes)
						.filter(attribute => attribute.retail_price > 0)
				return validAttributes.length > 0;
			}
			else {
				return this.localPass.retail_price > 0
			}
		},
		refundPolicyHint() {
			return this.refundPolicyItems
				.find(({ value }) => value === this.refundPolicyKey)
				.hint ||
				'';
		},
		isFeeTypeAmount: function () {
			return this.localPass.convenience_fee_type === 'fixed';
		},
		isFeeTypePercent: function () {
			return this.localPass.convenience_fee_type === 'percent'
		},
		isFormValid() {
			return this.hasPrices;
		},
		refundPolicyKey: {
			get() {
				return this.localPass.pass_settings?.refund_policy_key || '';
			},
			set(v) {
				const { pass_settings: settings } = this.localPass;

				if (!settings || Array.isArray(settings)) {
					this.$set(this.localPass, 'pass_settings', {});
				}

				this.$set(this.localPass.pass_settings, 'refund_policy_key', v);
			}
		},
		usePricingTiers() {
			return this.pricingStrategy === 'tiers';
		}
	},
	methods: {
		refundPolicyChange(v) {
			const isTimeframe = /^Refund requests must be made within .* before the pass expires\.$/;

			if (this.refundPolicyKey === 'timeframe' && isTimeframe.test(v.trim()))
				return;

			this.refundPolicyKey = '';
		},
		refundPolicyKeyChange(key) {
			this.localPass.refund_policy = this.refundPolicyItems
				.find(({ value }) => value === key)
				.policy;
		},
		savePrices() {
			this.isSaving = true;
			if(this.pricingStrategy === 'pricing') {
				this.localPass.attributes = [];
			}
			else {
				this.localPass.attributes = Object.values(this.attributes).filter(attribute => attribute.retail_price !== null)
				if(this.localPass.attributes) {
					let sortOrder = 0;
					this.localPass.attributes.forEach(attribute => {
						attribute.sort_order = sortOrder++;
					})
				}
				this.localPass.retail_price = null;
			}

			this.saveDestination();
		},
	},
};


</script>

<style scoped>
.dropdown-item-content {
	padding: 4px 8px; /* Adjust this as needed */
	white-space: nowrap; /* This ensures the content doesn't wrap and expands the width naturally */
}
</style>
